import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import EditorSection from '../LoopContentEditor/EditorSection';
import FooterSection from './FooterSection';
import HeaderSection from './HeaderSection';
import TopSection from '../LoopContentEditor/TopSection';
import useApi from '../../hooks/useApi';
import {
  debounce,
  setStatusSaving,
  setStatusSaved,
  setStatusSaveFailed,
  getAutoSaveInterval,
  getImageUrl,
  triggerEvent,
} from '../../utils';

import { PanelWrapper, ContentSection } from './styles';

const HubContentEditor = ({ nodeId, editor, panel, fileService, flash }) => {
  const { options, useAutoSave } = editor;
  const { userToken, integrations, circle_uuid, circle_state: state } = options;

  const { getData, loading } = useApi(userToken);
  const { putData } = useApi(userToken);

  const [data, setData] = useState();
  const [payload, setPayload] = useState({});
  const [updatedAt, setUpdatedAt] = useState();
  const [isUpdating, setIsUpdating] = useState(true);
  const [translations, setTranslations] = useState();
  const [showSaveButton, setShowSaveButton] = useState(!useAutoSave);

  const isSession = state === 'session';

  useEffect(() => {
    const getHubData = async () => {
      if (!loading) {
        const response = await getData(`/api/v3/editor/circles/${circle_uuid}/hub/${nodeId}`);
        if (response.success) {
          const { hub, translations } = response.body;
          setData(hub);
          setTranslations(translations);
          setUpdatedAt(hub.updated_at);
        }
      }
    };
    getHubData();
  }, []);

  useEffect(() => {
    if (data) {
      setIsUpdating(false);
    }
  }, [data]);

  useEffect(() => {
    document.addEventListener('useAutoSave', handleAutoSave);
    return () => document.removeEventListener('useAutoSave', handleAutoSave);
  }, []);

  const handleAutoSave = (ev) => {
    setShowSaveButton(!ev.detail.useAutoSave);
  };

  const debouncedSave = useCallback(
    debounce(async (item) => {
      await save(item);
    }, getAutoSaveInterval()),
    [],
  );

  useEffect(() => {
    if (useAutoSave && updatedAt && !loading) {
      setStatusSaving();
      if (payload?.immediateSave && !isUpdating) {
        save({ payload, updatedAt });
      } else {
        setIsUpdating(true);
        debouncedSave({ payload, updatedAt });
      }
    }
  }, [JSON.stringify(payload)]);

  const forceSave = async () => {
    setStatusSaving();
    await save({ payload, updatedAt });
  };

  const triggerEvents = (payload) => {
    const uuid = nodeId;
    const { lock_state, code, name, image } = payload;
    triggerEvent('lock.updated', { uuid, lock_state, code });
    triggerEvent('hub.updated', { uuid, name, fileName: getImageUrl(fileService, image, '220x220') });
  };

  const save = async ({ payload, updatedAt }) => {
    const hubData = {
      ...payload,
      updated_at: new Date(updatedAt).valueOf(),
    };
    const response = await putData(`/api/v3/editor/circles/${circle_uuid}/hub/${nodeId}`, hubData);
    if (response.success) {
      setStatusSaved();
      setIsUpdating(false);
      const { updated_at, description, inventory, ...data } = response.body;
      setUpdatedAt(updated_at);
      setData(data);
      if (!useAutoSave) {
        triggerEvents(payload);
      }
    } else {
      setStatusSaveFailed(i18n.__('behavior_save_failed'));
      setIsUpdating(false);
      console.error(response);
      if (response.message === 'auth_failed') {
        flash(i18n.__('app_error_unauthorized'), { ttl: 5000 });
      } else if (response.message === 'edit_conflict') {
        flash(i18n.__('error_edit_conflict_behavior'), { ttl: 5000 });
      } else if (response.message === 'payload_too_large') {
        flash(i18n.__('error_payload_too_large'), { ttl: 20000 });
      } else {
        flash(i18n.__('error_behavior_save_failed'));
      }
    }
  };

  if (loading || !translations) {
    return null;
  }

  return (
    <PanelWrapper>
      <TopSection panel={panel} loading={loading} translations={translations} />
      <HeaderSection
        editor={editor}
        data={data}
        payload={payload}
        setPayload={setPayload}
        fileService={fileService}
        loading={loading}
      />
      <ContentSection>
        <EditorSection
          editor={editor}
          userToken={userToken}
          integrations={integrations}
          fileService={fileService}
          data={data}
          payload={payload}
          setPayload={setPayload}
          loading={loading}
          translations={translations}
          circle_uuid={circle_uuid}
          type="hub"
        />
      </ContentSection>
      <FooterSection
        editor={editor}
        flash={flash}
        data={data}
        payload={payload}
        setPayload={setPayload}
        save={forceSave}
        translations={translations}
        showSaveButton={showSaveButton}
        isSession={isSession}
        panel={panel}
      />
    </PanelWrapper>
  );
};

export default HubContentEditor;

HubContentEditor.propTypes = {
  nodeId: PropTypes.string.isRequired,
  editor: PropTypes.object.isRequired,
  panel: PropTypes.object.isRequired,
  fileService: PropTypes.object.isRequired,
  flash: PropTypes.func.isRequired,
};
